import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Contacts, EmailType, PhoneType } from '@capacitor-community/contacts';
import { CreateContactOptions } from '@capacitor-community/contacts/dist/esm/definitions';
import { Capacitor } from '@capacitor/core';
import { AlertController } from '@ionic/angular';
import { MaskitoOptions } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import { OpenOptions } from 'capacitor-email-composer';
import { compact, isEqual } from 'lodash-es';
import { combineLatest, Observable } from 'rxjs';

import { phoneMask } from '../../../constants/maskito.constants';
import { MainTabBasePathEnum } from '../../../enums/main-tabs';
import * as fromLayoutSelectors from '../../../store/selectors/layout.selectors';
import * as fromOfficeSelectors from '../../../store/selectors/office.selectors';
import * as fromOrganizationSelectors from '../../../store/selectors/organization.selectors';
import * as fromTitleRepSelectors from '../../../store/selectors/title-rep.selectors';
import * as fromUserSelectors from '../../../store/selectors/user.selectors';
import { EmailComposerService } from '../../services/email-composer.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-contact-card-content',
  standalone: false,
  styleUrls: ['./contact-card-content.component.scss'],
  templateUrl: './contact-card-content.component.html'
})
export class ContactCardContentComponent {
  private alertController = inject(AlertController);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private emailComposerService = inject(EmailComposerService);
  private router = inject(Router);
  private store = inject(Store);

  readonly phoneMask: MaskitoOptions = phoneMask;

  contactInfo$: Observable<{
    company: string | null;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    phoneType: string | null;
    photoUrl: string | null;
  }>;

  contactCardPhotoUrl$: Observable<string | null>;

  useMobileLayout$: Observable<boolean>;

  emailOptions: OpenOptions | null = null;

  contactOptions: CreateContactOptions | null = null;

  shareContactInfoUrl = '/property-search/share-contact-info';

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (
          (
            [
              MainTabBasePathEnum.PropertySearch,
              MainTabBasePathEnum.WalkingFarm,
              MainTabBasePathEnum.Calculators,
              MainTabBasePathEnum.NetSheet,
              MainTabBasePathEnum.More
            ] as string[]
          ).includes(event.url)
        ) {
          const shareContactInfoUrl = `${event.url}/share-contact-info`;
          if (!isEqual(this.shareContactInfoUrl, shareContactInfoUrl)) {
            this.shareContactInfoUrl = shareContactInfoUrl;
            this.changeDetectorRef.detectChanges();
          }
        }
      }
    });

    this.contactInfo$ = this.store.pipe(select(fromTitleRepSelectors.selectContactInfo));
    this.contactCardPhotoUrl$ = this.store.pipe(select(fromOfficeSelectors.selectContactCardPhotoUrl));
    combineLatest([
      this.store.pipe(select(fromTitleRepSelectors.selectContactInfo)),
      this.store.pipe(select(fromUserSelectors.selectDetails)),
      this.store.pipe(select(fromOrganizationSelectors.selectApplicationName))
    ]).subscribe(([{ company, email, firstName, lastName, phone }, userDetails, applicationName]) => {
      this.contactOptions = {
        contact: {
          emails: [
            {
              address: email,
              label: 'work',
              type: EmailType.Work
            }
          ],
          name: {
            family: lastName,
            given: firstName
          },
          organization: {
            company
          },
          phones: [
            {
              label: 'work',
              number: phone,
              type: PhoneType.Work
            }
          ]
        }
      };
      this.emailOptions = {
        body: compact([
          'Hi,\n',
          '\n',
          `${userDetails.firstName} ${userDetails.lastName}`,
          userDetails.company,
          userDetails.phone,
          userDetails.email
        ]).join('\n'),
        cc: [userDetails.email || ''],
        subject: `${applicationName ?? 'SmartDirect&#174; Mobile'}: Message`,
        to: [email || '']
      };
    });

    this.useMobileLayout$ = this.store.pipe(select(fromLayoutSelectors.selectUseMobileLayout));
  }

  private async checkOrRequestContactPermission() {
    let permissionGranted = false;
    if (Capacitor.isNativePlatform()) {
      const { contacts } = await Contacts.checkPermissions();
      if (contacts !== 'granted') {
        const { contacts } = await Contacts.requestPermissions();
        if (contacts === 'granted') {
          permissionGranted = true;
        }
      } else {
        permissionGranted = true;
      }
    }
    return permissionGranted;
  }

  private async saveContact() {
    if (this.contactOptions) {
      const alertConfirm = await this.alertController.create({
        backdropDismiss: false,
        buttons: [
          {
            role: 'cancel',
            text: 'Cancel'
          },
          {
            role: 'confirm',
            text: 'Confirm'
          }
        ],
        header: 'Confirm',
        message: `Are you sure you want to add this contact information to your phone ?`
      });
      await alertConfirm.present();
      const { role } = await alertConfirm.onDidDismiss();
      if (role === 'confirm') {
        await Contacts.createContact(this.contactOptions);
        const alert = await this.alertController.create({
          backdropDismiss: false,
          buttons: [
            {
              role: 'cancel',
              text: 'Ok'
            }
          ],
          header: 'Success',
          message: `Contact was added to your phone.`
        });
        await alert.present();
      }
    }
  }

  async email(event: MouseEvent) {
    this.stopPropagation(event);
    if (this.emailOptions) {
      await this.emailComposerService.composeEmail(this.emailOptions);
    }
  }

  stopPropagation(event: MouseEvent) {
    event.stopImmediatePropagation();
    event.stopPropagation();
  }

  async addToContacts(event: MouseEvent) {
    this.stopPropagation(event);
    if (Capacitor.isNativePlatform() && this.contactOptions) {
      if (await this.checkOrRequestContactPermission()) {
        await this.saveContact();
      } else {
        const alert = await this.alertController.create({
          backdropDismiss: false,
          buttons: [
            {
              role: 'cancel',
              text: 'Ok'
            }
          ],
          header: 'Permission Denied',
          message: `In order to use this feature you need to grant contacts access permissions.`
        });
        await alert.present();
      }
    }
  }
}
